import { QueryOptions, useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { APITypes, AuditLogs } from '../../primary-record-types';
import { appAPI } from '../../utils/api';
import urls from '../../utils/urls';
import { useMemo } from 'react';
import { useRefreshOnFocus } from '../useRefetchOnFocus';
import { getAuditLogTypeQueryCacheKey, onAuditLogMessage } from './cacheKeys';
import { useLoadedUser } from './useUser';
import { getIntl } from '../../utils/intl';
import useNotify from '../useNotify';

const intl = getIntl('useAuditLogs', {
    sendMessageSuccess: 'Message sent',
    sendMessageError: 'Error sending message',
});

export type UseAuditLogsArgs = {
    medicalProfileIds: string[];
    auditLogTypes: AuditLogs.AuditLogTypes[];
    onSendMessageSuccess?: () => void;
};

export type SendMessageArgs = {
    medicalProfileIds: string[];
    message: string;
};

export type UseAuditLogReturnValue = {
    auditLogs: APITypes.auditLogService.AuditLogListItem[];
    isLoading: boolean;
    sendMessage: (args: SendMessageArgs) => void;
    isSendingMessage: boolean;
};

function getSingleReference<TReference>(
    reference: AuditLogs.SingleOrListAuditLogReferenceValue<TReference>,
): AuditLogs.SingleAuditLogReferenceValue<TReference> {
    if (Array.isArray(reference)) {
        console.error('Expected single reference but got an array', reference);
        return reference[0];
    } else {
        return reference;
    }
}

export const useAuditLogs = (args: UseAuditLogsArgs) => {
    const { medicalProfileIds, auditLogTypes } = args;
    const queryClient = useQueryClient();
    const { user } = useLoadedUser({});
    const notify = useNotify();
    const queries = medicalProfileIds.map<
        QueryOptions<
            APITypes.auditLogService.QueryAuditLogsResponse,
            any,
            APITypes.auditLogService.QueryAuditLogsResponse
        >
    >((medicalProfileId) => ({
        queryKey: getAuditLogTypeQueryCacheKey(medicalProfileId, args.auditLogTypes),
        queryFn: async () => {
            const response = await appAPI.post<APITypes.auditLogService.QueryAuditLogsResponse>(
                urls.api.auditLogs.profileAuditLogsSearch(medicalProfileId),
                {
                    body: {
                        medicalProfileId,
                        auditLogTypes,
                    },
                },
            );

            return response;
        },
    }));
    useRefreshOnFocus(() => queries.forEach((query) => queryClient.refetchQueries({ queryKey: query.queryKey })));
    const queryResults = useQueries({ queries: queries as any });
    const isLoading = queryResults.some((queryResult) => queryResult.isLoading);
    const sortedAuditLogs = useMemo(() => {
        if (isLoading) return [];

        const allAuditLogs: APITypes.auditLogService.AuditLogListItem[] = [];
        queryResults.filter(Boolean).forEach((queryResult) => {
            if (queryResult.data) {
                allAuditLogs.push(
                    ...(queryResult.data as APITypes.auditLogService.QueryAuditLogsResponse).auditLogs.filter(
                        (_) =>
                            !(
                                user.type !== 'professional' &&
                                _.type === 'user-profile-view' &&
                                getSingleReference(_.references.user).id === user.userId
                            ), // own profile views are useful to professionals, but noise in the family app
                    ),
                );
            }
        });

        return allAuditLogs.sort((a, b) => b.timestamp - a.timestamp);
    }, [isLoading, queryResults, user.type, user.userId]);

    const sendMessage = useMutation({
        mutationFn: async (args: SendMessageArgs) => {
            const submitProfileMessage = async (medicalProfileId: string) => {
                const body: APITypes.auditLogService.AddProfileMessageAuditLogRequest = {
                    type: 'profile-message',
                    message: args.message,
                };
                await appAPI.post(urls.api.auditLogs.auditLogMessage(medicalProfileId), { body });
            };

            await Promise.all(args.medicalProfileIds.map(submitProfileMessage));
        },
        onSuccess: (_response, input) => {
            notify('success', intl('sendMessageSuccess'));

            onAuditLogMessage.resetCache({ medicalProfileIds: input.medicalProfileIds })();
            args.onSendMessageSuccess?.();
        },
        onError: () => {
            notify('error', intl('sendMessageError'));
        },
    });

    return {
        auditLogs: sortedAuditLogs,
        isLoading,
        isSendingMessage: sendMessage.isPending,
        sendMessage: sendMessage.mutate,
    };
};
